import React, {useState} from 'react';
import {
    Card,
    CardHeader,
    CardContent,
    Table,
    TableBody,
    TableRow,
    TableCell,
    Typography,
    makeStyles,
	Button,
	Box,
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import {Link} from "react-router-dom";
import ChangeAddressDialog from './ChangeAddressDialog';

const useStyles = makeStyles((theme) => ({
    table: {
        borderCollapse: 'separate',
        borderSpacing: 0,
        '& td, & th': {
            borderBottom: 'none',
            padding: '8px 12px',
            verticalAlign: 'top',
        },
        '& .MuiTableRow-root': {
            '&:hover': {
                backgroundColor: theme.palette.action.hover,
            },
        },
    },
    labelCell: {
        fontWeight: 'bold',
        width: '20%',
        whiteSpace: 'nowrap',
    },
	changeAddressBtn: {
		marginLeft: 8,
		padding: '2px 8px',
		fontSize: '0.75rem',
		textTransform: 'none',
		minWidth: "100px",
	},
	pendingBadge: {
		backgroundColor: '#ff9800',
		color: 'white',
		padding: '2px 6px',
		borderRadius: 4,
		fontSize: '0.7rem',
		marginLeft: 8,
	},
	pendingAddress: {
		backgroundColor: '#fff3e0',
		padding: '8px',
		borderRadius: 4,
		marginTop: 8,
		border: '1px dashed #ff9800',
	},
	originalAddress: {
		textDecoration: 'line-through',
		color: '#999',
	},
}));

const OrderCustomerTable = ({details, customer, address, onAddressChanged, pendingAddress}) => {
    const classes = useStyles();
	const [showAddressDialog, setShowAddressDialog] = useState(false);

	if (!details) return null;

	const customerData = customer(details);
	const addressData = address(details);
	const {
		house_and_street,
		house_number,
		street_details,
		apartment_name,
		commune,
		district,
		city,
		area_details,
  } = addressData || {};

	const formatAddressObj = (addr) => {
		if (!addr) return '';
		return [
			addr.house_and_street || `${addr.house_number || ''} ${addr.street_details || ''}`.trim(),
			addr.apartment_name,
			addr.commune,
			addr.district,
			addr.city,
			addr.area_details,
		].filter(Boolean).join(', ');
	};

	const fullAddress = formatAddressObj(addressData);
	const pendingFullAddress = pendingAddress ? formatAddressObj(pendingAddress) : null;
	const hasPendingChange = pendingAddress && pendingAddress.address_id !== addressData?.address_id;

    return (
        <Card className="mb-3">
            <CardHeader title={<Typography variant="h6"><b>CUSTOMER DETAILS</b></Typography>} />
            <CardContent>
                <Table className={classes.table}>
                    <TableBody>
                        <TableRow>
                            <TableCell className={classes.labelCell}>Name</TableCell>
	                        <TableCell>
		                        <Link
			                        target="_blank"
			                        to={{
				                        pathname: "/admin/customers",
				                        search: `?search=${encodeURIComponent(customer(details).name)}`
			                        }}>
			                        {customerData.name}
		                        </Link>
	                        </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell className={classes.labelCell}>Email</TableCell>
                            <TableCell>{customerData.email}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell className={classes.labelCell}>Phone</TableCell>
                            <TableCell>{customerData.phone}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell className={classes.labelCell}>Delipoint Tier</TableCell>
                            <TableCell>{customerData.delipoints_tier ? customerData.delipoints_tier.name + ' ( x ' + customerData.delipoints_tier.multiplier + ')' : '-'}</TableCell>
                        </TableRow>
                        <TableRow>
	                        <TableCell className={classes.labelCell}>
		                        Address
		                        {hasPendingChange && <span className={classes.pendingBadge}>Pending</span>}
	                        </TableCell>
	                        <TableCell>
		                        <Box display="flex" alignItems="flex-start" flexDirection="column">
			                        <Box display="flex" alignItems="center">
                                        <span className={hasPendingChange ? classes.originalAddress : ''}>
                                            {fullAddress}
                                        </span>
				                        <Button
					                        size="small"
					                        variant="outlined"
					                        color="primary"
					                        startIcon={<EditIcon fontSize="small"/>}
					                        className={classes.changeAddressBtn}
					                        onClick={() => setShowAddressDialog(true)}
				                        >
					                        Change
				                        </Button>
			                        </Box>
			                        {hasPendingChange && (
				                        <Box className={classes.pendingAddress}>
					                        <Typography variant="caption" style={{fontWeight: 'bold', color: '#e65100'}}>
						                        New Address (will be applied on Save):
					                        </Typography>
					                        <Typography variant="body2">
						                        {pendingFullAddress}
					                        </Typography>
				                        </Box>
			                        )}
		                        </Box>
	                        </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell className={classes.labelCell}>Distance From Shop</TableCell>
                            <TableCell>{details?.total_kms ? `${details.total_kms} km` : '-'}</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </CardContent>

	        {/* Change Address Dialog */}
	        <ChangeAddressDialog
		        open={showAddressDialog}
		        onClose={() => setShowAddressDialog(false)}
		        customerId={customerData?.cust_id}
		        currentAddressId={addressData?.address_id}
		        orderId={details?.order_id}
		        onAddressChanged={onAddressChanged}
		        customerData={customerData}
	        />
        </Card>
    );
};

export default OrderCustomerTable;
